import { envVars } from '@/lib/environment/config';
import type {
  InfinexEnvironment,
  SponsorshipMode,
} from '@infinex-private/connect-types';
import { StatusWarningIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Button, Surface } from '@infinex/ui/components';

import type { UserNativeBalanceDetails } from '../../types';

interface InsufficientFundsWarningProps {
  selectedPaymentMethod: SponsorshipMode;
  userBalance?: UserNativeBalanceDetails;
  onTopUpGasAccount?: () => void;
}

export function InsufficientFundsWarning({
  selectedPaymentMethod,
  userBalance,
  onTopUpGasAccount,
}: InsufficientFundsWarningProps) {
  const gasToken = userBalance?.symbol || 'ETH';
  const isGasAccount = selectedPaymentMethod === 'account';

  const platformUrl = (() => {
    const env = envVars.INFINEX_ENVIRONMENT as InfinexEnvironment;
    if (env === 'prod') return 'https://app.infinex.xyz';
    if (env === 'staging') return 'https://staging.app.infinex.xyz';
    if (env === 'test') return 'https://test.app.infinex.xyz';
    return 'https://app.local';
  })();

  const handleTopUpPress = () => {
    if (isGasAccount && onTopUpGasAccount) {
      onTopUpGasAccount();
    } else {
      const url = isGasAccount
        ? `${platformUrl}?action=gas-account`
        : `${platformUrl}/swidge`;
      window.open(url, '_blank');
      window.close();
    }
  };

  return (
    <Surface
      tone="caution"
      rounded="medium"
      className="flex items-center justify-between p-4"
    >
      <div className="flex items-center gap-4">
        <StatusWarningIcon fill="caution" size="medium" />
        <div className="text-default flex flex-col text-sm">
          {isGasAccount ? (
            'Please top up your gas account to continue.'
          ) : (
            <>
              <span>Not enough {gasToken} for gas.</span>
              <span>Swidge to top up</span>
            </>
          )}
        </div>
      </div>
      <Button
        prominence="default"
        size="small"
        className={cn('rounded-xl py-1', isGasAccount ? 'px-6' : 'px-4')}
        onPress={handleTopUpPress}
      >
        {isGasAccount ? 'Top up' : 'Swidge'}
      </Button>
    </Surface>
  );
}
